import { track } from "./index";
import { MAX_ERRORS_PER_PAGE } from "./constants";

let reported = 0;
const seen = new Set<string>();

/**
 * Guards the error events against the two ways they flood: a render loop firing
 * the same error forever, and a page that is simply broken in many places.
 * Reset per page load rather than per route change, since a loop survives
 * navigation.
 */
export const shouldReportError = (signature: string): boolean => {
  if (reported >= MAX_ERRORS_PER_PAGE || seen.has(signature)) return false;

  seen.add(signature);
  reported += 1;
  return true;
};

let installed = false;

/** Reports uncaught exceptions and unhandled promise rejections. */
export const installErrorTracking = (): void => {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    const message = event.message || "Unknown error";
    if (!shouldReportError(`error:${message}`)) return;

    track("js_error", {
      message,
      // Bundle coordinates only — enough to tell two errors apart without
      // source maps, which this is not a substitute for.
      source: event.filename || "",
      line: event.lineno ?? 0,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : String(reason ?? "Unknown rejection");

    if (!shouldReportError(`rejection:${message}`)) return;

    track("js_error", { message, kind: "unhandledrejection" });
  });
};
